'use client';

/**
 * TestimonialsSection — landing section (SPEC-136 §5).
 *
 * Section structure (VERBATIM from §5):
 *   - <section id="testimonios"> with header (h2 + subtitle).
 *   - Grid: 3 cols desktop, 2 tablet, 1 mobile.
 *   - Top 6 testimonials with isFeatured=true sorted by displayOrder ASC,
 *     then publishedAt DESC.
 *   - CTA below the grid: "Comparte tu historia →" → /app/testimonios
 *     (if logged in) or /register (if not).
 *
 * Schema.org Organization (§5 + §13 — P-227 fix SPEC-139):
 *   JSON-LD script tag with `@type: Organization` + `name: "Profileia"`.
 *   The previous `aggregateRating` sub-object was REMOVED because it
 *   showed `ratingValue: "5.0"` with `reviewCount: 0` (false advertising
 *   — Google penalizes structured data spam). When the testimonios
 *   feature gets a real rating system (5 stars per testimonio + ≥1
 *   review published), the aggregateRating can be re-added (SPEC separado).
 *
 * §6 Anti-Invención: copy "historias reales" NOT "testimonios de ganancias
 * garantizadas" (per §13). NO promise of earnings.
 *
 * Spec ref: docs/SPEC-136-TESTIMONIOS.md §5 (UI/UX), §13 (§6 Anti-Invención).
 *
 * Anti-P-023: fetches real testimonials (no claim PASS).
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` statements.
 */
import { useEffect, useState, type ReactElement } from 'react';
import Link from 'next/link';
import { TestimonialCard } from './TestimonialCard';
import { Button } from '@/components/ui/button';
import { listPublicTestimonials } from '@/lib/api-testimonials';
import { useAuth } from '@/lib/use-auth';
import { ApiClientError } from '@/lib/api-client';
import type { PublicTestimonial } from '@profileia/shared';

const FEATURED_LIMIT = 6;

export function TestimonialsSection(): ReactElement {
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const data = await listPublicTestimonials({ limit: FEATURED_LIMIT, featured: true });
        if (cancelled) return;
        setTestimonials(data);
      } catch (err) {
        if (cancelled) return;
        // Graceful degradation — landing section shows error message instead
        // of crashing the whole page. The rest of the home page is unaffected.
        if (err instanceof ApiClientError) {
          setError(err.message || 'No pudimos cargar los testimonios.');
        } else {
          setError('No pudimos cargar los testimonios. Intenta más tarde.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // P-227 fix (SPEC-139 — Sprint 63): the previous version embedded an
  // `aggregateRating` sub-object with `ratingValue: "5.0"` + `reviewCount:
  // testimonials.length` (often 0 during loading/empty state) +
  // `bestRating: "5"` + `worstRating: "5"`. This is misleading structured
  // data — Google penalizes "structured data spam" for showing 5-star
  // ratings when there are 0 reviews (false advertising). Plus
  // `bestRating === worstRating` is meaningless per Schema.org spec.
  //
  // SPEC-139 §5.2 Opción A: REMOVER el bloque `aggregateRating` del
  // JSON-LD hasta que los testimonios tengan sistema de rating real
  // (5 estrellas por testimonio) y al menos 1 review publicado.
  // Cuando se implemente rating real (SPEC separado), se vuelve a
  // agregar. Mientras tanto, el JSON-LD solo tiene `{ "@context",
  // "@type": "Organization", "name": "Profileia" }` (sin aggregateRating).
  //
  // Anti-§6: NO se inventa rating real — solo se REMUEVE el bloque
  // engañoso. AC-3: "Si se remueve: el JSON-LD solo tiene `{ "@type":
  // "Organization", "name": "Profileia" }` sin aggregateRating".
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Profileia',
  };

  return (
    // SPEC-177 Task F (Sprint 91): bg-slate-50 → bg-muted/30.
    // The hardcoded `bg-slate-50` had no `dark:` variant, so in dark mode
    // the section rendered as a bright white slab against the dark
    // background — visually broken + inaccessible (contrast ratio of
    // white text on slate-50 in dark mode is fine, but the section
    // itself is a jarring bright island). `bg-muted/30` uses the shadcn
    // muted token which has both light + dark variants — seamless in
    // both themes. Aligns with the rest of the landing (problem section,
    // pricing section) which already use `bg-muted/30`.
    <section id="testimonios" className="py-20 bg-muted/30">
 {/* P-227 fix (): JSON-LD Organization (no aggregateRating). */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger -- JSON-LD is a static
        // serialized object (no user input — safe). This is the standard
        // Next.js pattern for embedding structured data.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />

      <div className="max-w-7xl mx-auto px-6">
 {/* b (Sprint 106b fix): replaced hardcoded `text-slate-900` /
            `text-slate-600` / `text-slate-500` with semantic tokens
            (`text-foreground` / `text-muted-foreground`) so the section
            has correct contrast in BOTH light and dark mode. The previous
            hardcoded slate-* values had no `dark:` variants, causing
            dark-on-dark text in dark mode (Founder-reported bug). */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-foreground">
          Historias reales de emprendedores con Profileia
        </h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          No son actores ni escenarios. Son emprendedores construyendo su negocio con Profileia.
          Esto es lo que ellos cuentan.
        </p>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: FEATURED_LIMIT }).map((_, i) => (
              <div
                key={i}
                className="bg-card border border-border p-6 h-64 animate-pulse rounded-2xl"
                aria-hidden="true"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded" />
                  <div className="h-3 bg-muted rounded" />
                  <div className="h-3 bg-muted rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground/70">
              Vuelve a cargar la página o intenta más tarde.
            </p>
          </div>
        )}

        {!loading && !error && testimonials.length === 0 && (
          <div className="text-center py-12">
 {/* (Sprint 103 — Casa Unificada): empty state alineado
                a la decisión #4 del Founder. Los testimonios ficticios
                anteriores fueron removidos del seed-testimonials.ts. Esta
                sección ahora muestra "Testimonio pronto" hasta que existan
                testimonios reales con consentimiento de los usuarios. */}
            <p className="text-foreground mb-4 font-semibold">
              Testimonio pronto
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Estamos construyendo Profileia con emprendedores reales. En cuanto
              tengamos historias verificables con consentimiento, las compartiremos
              aquí. Si usas Profileia y quieres compartir tu experiencia,
              <Link href="/app/testimonios" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline ml-1">
                compártela aquí
              </Link>.
            </p>
          </div>
        )}

        {!loading && !error && testimonials.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <TestimonialCard key={t._id} testimonial={t} />
            ))}
          </div>
        )}

 {/* CTA below the grid (§5) */}
        <div className="text-center mt-12">
          {user ? (
            <Link href="/app/testimonios">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Comparte tu historia →
              </Button>
            </Link>
          ) : (
            <Link href="/register">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Comparte tu historia →
              </Button>
            </Link>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            Si ya eres usuario de Profileia, publica tu testimonio. Si no,{' '}
            <Link href="/register" className="text-emerald-600 underline">
              crea tu cuenta gratis
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
